import React, { useEffect } from 'react';
import {
    Box,
    InputBase,
    MenuItem,
    Select,
    Toolbar,
    Typography
} from '@mui/material';
import Button from '@mui/material/Button';
import i18n from 'i18';

export function CustomTablePagination(props) {
    const {
        count,
        page,
        rowsPerPage,
        rowsPerPageOptions = [5, 10, 25, 100],
        labelRowsPerPage = 'rowsPerPage',
        onPageChange,
        onRowsPerPageChange
    } = props;
    const [currentPage, setCurrentPage] = React.useState(page + 1);
    const [typingTimeout, setTypingTimeout] = React.useState(null);

    useEffect(() => {
        setCurrentPage(page + 1);
        if (typingTimeout) clearTimeout(typingTimeout);
    }, [page]);

    const totalPages = Math.ceil(count / rowsPerPage);

    const handleBackButtonClick = (event) => {
        onPageChange(event, page - 1);
    };

    const handleNextButtonClick = (event) => {
        onPageChange(event, page + 1);
    };

    const handleChangeRowsPerPage = (event) => {
        onRowsPerPageChange(event);
    };

    const handlePageUpdate = (input) => {
        if (!/^\d+$/.test(input)) {
            setCurrentPage(page + 1);
            return;
        }
        const newPage = Number(input);
        if (newPage < 1 || newPage > totalPages) {
            setCurrentPage(page + 1);
        } else if (newPage !== page + 1) {
            onPageChange(null, newPage - 1);
        }
    };

    const handlePageChange = (event) => {
        setCurrentPage(event.target.value);
        if (typingTimeout) clearTimeout(typingTimeout);
        setTypingTimeout(
            setTimeout(() => {
                handlePageUpdate(event.target.value);
            }, 1000)
        );
    };

    return (
        <Toolbar
            sx={{
                backgroundColor: (theme) => theme.palette.background.default,
                borderTop: (theme) => `1px solid ${theme.palette.divider}`
            }}
        >
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                width="100%"
                flex="1 1 100%"
            >
                <Box display="flex" gap={2} alignItems="center">
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={handleBackButtonClick}
                        disabled={page === 0}
                    >
                        {i18n.t('buttons.previous')}
                    </Button>
                    <Typography variant="subtitle2" color="text.default">
                        {i18n.t('text.page')}
                    </Typography>

                    <Box
                        sx={{
                            minWidth: '32px',
                            maxWidth: '48px',
                            height: '36px',
                            padding: '3px 12px',
                            border: (theme) =>
                                `1px solid ${theme.palette.grey[300]}`,
                            borderRadius: '8px'
                        }}
                    >
                        <InputBase
                            value={currentPage}
                            onChange={handlePageChange}
                            sx={{
                                textAlign: 'center',
                                width: '100%',
                                padding: 0,
                                fontSize: currentPage > 99 ? '0.75rem' : '1rem',
                                color: 'inherit',
                                backgroundColor: 'transparent',
                                border: 'none',
                                outline: 'none'
                            }}
                            inputProps={{
                                style: { textAlign: 'center' }
                            }}
                        />
                    </Box>

                    <Typography variant="subtitle2" color="text.default">
                        {i18n.t('text.of')} {totalPages}
                    </Typography>

                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={handleNextButtonClick}
                        disabled={page >= totalPages - 1}
                    >
                        {i18n.t('buttons.next')}
                    </Button>
                </Box>
                <Box
                    display="flex"
                    gap={2}
                    alignItems="center"
                    sx={{
                        // if screen size is small, hide the box
                        display: { xs: 'none', sm: 'flex' }
                    }}
                >
                    <Typography variant="subtitle2" color="text.default">
                        {i18n.t(`text.${labelRowsPerPage}`)}
                    </Typography>
                    <Select
                        value={rowsPerPage}
                        onChange={handleChangeRowsPerPage}
                        sx={{
                            '& .MuiSelect-select': {
                                padding: '6px 8px'
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: (theme) => theme.palette.grey[300]
                            },
                            '& .MuiSelect-icon': {
                                color: '#A0A0A0'
                            },
                            "borderRadius": '8px',
                            "minWidth": '32px'
                        }}
                        MenuProps={{
                            anchorOrigin: {
                                vertical: 'bottom',
                                horizontal: 'center'
                            },
                            transformOrigin: {
                                vertical: 'bottom',
                                horizontal: 'center'
                            },
                            PaperProps: {
                                sx: {
                                    "borderRadius": '8px',
                                    "boxShadow": '0px 4px 8px rgba(0, 0, 0, 0.1)',
                                    "padding": '0 12px',
                                    "minWidth": '32px',
                                    "backgroundColor": (theme) =>
                                        theme.palette.background.default,
                                    '& .MuiMenuItem-root': {
                                        "justifyContent": 'center',
                                        "borderRadius": '8px',
                                        "mt": '4px',
                                        '&:hover': {
                                            backgroundColor: (theme) =>
                                                theme.palette.action.hover
                                        }
                                    }
                                }
                            }
                        }}
                    >
                        {rowsPerPageOptions.map((option) => (
                            <MenuItem key={option} value={option}>
                                <Typography variant="button">
                                    {option}
                                </Typography>
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
            </Box>
        </Toolbar>
    );
}
